/** What the Vendors, Facilities and Reports places share, and nothing else (ADR-0212).
 *
 * Small on purpose: the label under the place's title, a search match, a client sort over the rows a
 * screen already holds, a count as the product's one number formatter writes it, and the three things
 * a table page does with a read that is not yet rows - say it is loading, say it failed, say there
 * is nothing. **A failed read is never the empty state** (EXP-29): `ReadFailed` is the one rendering
 * and the empty sentence is only reached when the read succeeded.
 */

import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useFiguresPaged } from "../api/queries";
import type { FigureCut } from "../api/queries";

import { quantity } from "../lib/format";
import { sortRows } from "../components/ui/sortRows";
import type { SortState } from "../components/ui/sortRows";
import { tabLabelAt } from "../shell/routeTable";
import { pagingOf, WHOLE_RECORDS_NOTE } from "./drawerFigures";
import styles from "./tableKit.module.css";
import type { Screen } from "./useScreen";
import { useScreen } from "./useScreen";

/** The tab's name for the title card's small label, from the route table (never typed here). */
export function usePlaceLabel(): string | undefined {
  const screen = useScreen();
  const { pathname } = useLocation();
  return tabLabelAt(pathname, screen.tab);
}

/** A count, written once: thousands separators, no decimals. */
export function count(value: number): string {
  return quantity(String(value));
}

/** Whether any of these fields holds what was typed in the table's own search box. */
export function matches(needle: string, ...fields: ReadonlyArray<string | null | undefined>): boolean {
  const wanted = needle.trim().toLowerCase();
  return wanted === "" || fields.some((field) => field?.toLowerCase().includes(wanted));
}

/** Order the rows a screen holds by the column the reader chose. The table reports the choice and
 * orders nothing (`GridTable`); a server-paged table would ask the server, and these tables hold
 * every row. */
export function ordered<T>(
  rows: readonly T[],
  sort: SortState | null,
  accessors: Readonly<Record<string, (row: T) => string | number | null | undefined>>,
): T[] {
  if (!sort) return [...rows];
  return sortRows(rows, accessors[sort.columnId], sort.direction);
}

/** The text of a failed or pending read for a `TablePage`'s `state` slot is each place's own; this
 * only holds the shared sentence-free pieces. */
export function Dash(): ReactNode {
  return <span aria-label="no figure">—</span>;
}

/** What a node of a table adds up to, and which figures it is made of: the breakdown drawer's cut.
 *
 * A number in a table that is a sum (a vendor's tonnage, a site's, a company's) must open the figures
 * it sums (rule 3). The key the number writes into the URL names the node (`site-<id>`,
 * `vendor-<id>`); the page resolves it to a cut and the server's own total for it, and the drawer
 * lists the figures a page at a time. A key that names nothing the page holds opens nothing.
 */
export interface NodeCut {
  label: string;
  cut: FigureCut;
  /** The server's total for the node, in kilograms. */
  headline: string | undefined;
}

export function useNodeBreakdown(screen: Screen, nodeOf: (key: string) => NodeCut | undefined) {
  const node = screen.breakdown ? nodeOf(screen.breakdown) : undefined;
  const read = useFiguresPaged(node ? screen.window : undefined, screen.method, node?.cut ?? {});
  if (!node) return {};
  return {
    breakdownFigures: read.figures,
    breakdownLabel: node.label,
    breakdownRead: read,
    breakdownPaging: pagingOf(read),
    breakdownHeadline: node.headline,
    breakdownNote: node.headline !== undefined ? WHOLE_RECORDS_NOTE : undefined,
  };
}

/** A tonnage that opens the figures behind it. A dash is not a figure and opens nothing. */
export function OpenFigures({
  screen,
  nodeKey,
  value,
  name,
}: {
  screen: Screen;
  nodeKey: string;
  /** The tonnage as written, or a dash for a node with no figure. */
  value: string;
  name: string;
}) {
  if (value === "—") return <>{value}</>;
  return (
    <button
      type="button"
      className={styles.figure}
      onClick={() => screen.openBreakdown(nodeKey)}
      aria-label={`${name}: ${value} tonnes. Show the figures behind it`}
    >
      {value}
    </button>
  );
}
